import { FieldKey } from '../../context/field-keys.js';
import { defaultRound } from '../../shared/round.js';
/**
 * Availability asking-rent kernels, ported from `availability-auto-calc-listener-helper.ts`.
 *
 *   yearlyTotal       = (includeOpex ? askingRent + opex : askingRent) × spaceSize  (see the gated rule below)
 *   askingRentPerSqm  = yearlyTotal / spaceSize                                    (addListenerToAskingRentPsqm)
 *
 * The full asking-rent matrix (TotalRentEurosPerMonth ⟷ AskingRentalRateMonthly ⟷ AskingRentYearlyTotal
 * ⟷ AskingRent, with ×12/÷12 between them) is interdependent and stays orchestrated in the Phase 4
 * listener, which calls these kernels. Outgoings are included for Australian Net records only —
 * see `askingRentYearlyTotalLocaleGatedRule`.
 */
/** (isPacific ? askingRent + opex : askingRent) × spaceSize. */
export function calculateAskingRentYearlyTotal(askingRent, opex, spaceSize, isPacific) {
  return (isPacific ? askingRent + opex : askingRent) * spaceSize;
}
/** yearlyTotal / spaceSize (null when spaceSize is 0/absent). */
export function calculateAskingRentPerSqm(yearlyTotal, spaceSize) {
  return yearlyTotal == null || !spaceSize ? null : Number(yearlyTotal) / Number(spaceSize);
}
const num = v => v == null ? null : Number(v);
/**
 * Yearly total from asking rent × space size, with the opex decision delegated to the caller via
 * `deps.isPacific`.
 *
 * NOT the rule to route for the product-specified behaviour: it has no Asking Rate Type input, so
 * it cannot distinguish Australian Net from Australian Gross, and `isPacific` is true for New
 * Zealand — which must never include outgoings. Route
 * `availability.asking-rent-yearly-total.locale-gated` instead, which carries the real gate.
 */
export const askingRentYearlyTotalRule = {
  id: 'availability.asking-rent-yearly-total.from-rent-size',
  inputs: [FieldKey.AvailAskingRent, FieldKey.AvailOpex, FieldKey.AvailSpaceSize],
  outputs: [FieldKey.AvailAskingRentYearlyTotal],
  formulaVersion: '1.0.0',
  run: (ctx, deps) => {
    const round = deps?.round ?? defaultRound;
    const rent = num(ctx.get(FieldKey.AvailAskingRent));
    const size = num(ctx.get(FieldKey.AvailSpaceSize));
    if (rent == null || size == null) {
      return;
    }
    const opex = num(ctx.get(FieldKey.AvailOpex)) ?? 0;
    const total = calculateAskingRentYearlyTotal(rent, opex, size, !!deps?.isPacific);
    ctx.set(FieldKey.AvailAskingRentYearlyTotal, round(total, FieldKey.AvailAskingRentYearlyTotal));
  }
};
/** Asking rent $/sqm from yearly total ÷ space size. */
export const askingRentPerSqmRule = {
  id: 'availability.asking-rent-per-sqm.from-yearly-total-size',
  inputs: [FieldKey.AvailAskingRentYearlyTotal, FieldKey.AvailSpaceSize],
  outputs: [FieldKey.AvailAskingRent],
  formulaVersion: '1.0.0',
  run: (ctx, deps) => {
    const round = deps?.round ?? defaultRound;
    const psqm = calculateAskingRentPerSqm(num(ctx.get(FieldKey.AvailAskingRentYearlyTotal)), num(ctx.get(FieldKey.AvailSpaceSize)));
    ctx.set(FieldKey.AvailAskingRent, psqm == null ? null : round(psqm, FieldKey.AvailAskingRent));
  }
};
/** `ref_rate_type_desc` value that selects the AUS-Net (opex-included) yearly-total branch. */
export const ASKING_RATE_TYPE_NET = 'Net';
/**
 * Yearly total, gated per the product specification confirmed 2026-08-21:
 *
 *   Australia, Asking Rate Type = Net   -> (askingRent + opex) × spaceSize
 *   Australia, Asking Rate Type = Gross -> askingRent × spaceSize
 *   New Zealand and every other market  -> askingRent × spaceSize
 *
 * i.e. `opex included when (isAus && rateType === 'Net')`, full stop.
 *
 * v2.0.0 — the previous gate was `(isAus && Net) || (isPacific && !isNz)`. Because Pacific is
 * AUS ∪ NZ, that second clause matched EVERY Australian record, so Asking Rate Type was ignored
 * for Australia and Gross records were inflated by the outgoings. Product confirmed Gross must
 * not include outgoings, so the clause is removed. Bulk edit's payload path already gated on
 * AUS+Net only, so this brings the package in line with it rather than the other way round.
 *
 * One deliberate consolidation vs the original listener, formula-neutral: the legacy AUS-Net
 * branch also required the space-size UOM control to hold a value. Rules are UOM-blind (T2.7 owns
 * UOM), and dropping that check is now REQUIRED for correctness — with the Pacific clause gone,
 * a null UOM would otherwise fall through to the plain branch and silently drop the outgoings
 * from an Australian Net record.
 *
 * `updateAskingRentYealyValue` wrote unconditionally (no NaN/finite guard); preserved here.
 */
export const askingRentYearlyTotalLocaleGatedRule = {
  id: 'availability.asking-rent-yearly-total.locale-gated',
  inputs: [FieldKey.AvailAskingRent, FieldKey.AvailOpex, FieldKey.AvailSpaceSize, FieldKey.AvailAskingRateType],
  outputs: [FieldKey.AvailAskingRentYearlyTotal],
  formulaVersion: '2.0.0',
  run: (ctx, deps) => {
    const round = deps?.round ?? defaultRound;
    const rent = num(ctx.get(FieldKey.AvailAskingRent));
    const size = num(ctx.get(FieldKey.AvailSpaceSize));
    if (rent == null || size == null) {
      return;
    }
    const opex = num(ctx.get(FieldKey.AvailOpex)) ?? 0;
    const rateType = ctx.get(FieldKey.AvailAskingRateType);
    const includeOpex = !!deps?.isAus && rateType === ASKING_RATE_TYPE_NET;
    const total = calculateAskingRentYearlyTotal(rent, opex, size, includeOpex);
    ctx.set(FieldKey.AvailAskingRentYearlyTotal, round(total, FieldKey.AvailAskingRentYearlyTotal));
  }
};
export const availabilityAskingRentRules = [askingRentYearlyTotalRule, askingRentPerSqmRule, askingRentYearlyTotalLocaleGatedRule];
